import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Chip,
  TextField,
  Autocomplete,
  FormHelperText,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ErrorBoundary from '../../components/@generalComponents/ErrorBoundary';
import getRandomString from '../../utils/getRandomString';

const MultiSelect = ({
  label,
  value = [],
  onChange,
  error,
  help,
  required = false,
  selections = [],
  fullWidth = false,
  disabled = false,
  size = 'medium',
  onBlur,
  field,
}) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState('');
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const dispatch = useDispatch();
  const businessStructure = useSelector(
    (state) => state.core.businessStructure
  );
  const structures = businessStructure?.structures;
  const firstServiceStructure = structures?.find(
    (s) => s.collectionField === 'services'
  )?.id;

  const handleChange = useCallback((event, newValue) => {
    // Extract only the values from the selected options
    const selectedValues = newValue.map(option => option.value);
    onChange(field?.value, selectedValues);
  }, [field, onChange]);

  const handleInputChange = useCallback((event, newInputValue) => {
    setInputValue(newInputValue);
  }, []);

  const getOptionLabel = useCallback((option) => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option[`label_${currentLang}`] || option.label || option.value || '';
  }, [currentLang]);

  const isOptionEqualToValue = useCallback((option, value) => {
    if (!option || !value) return false;
    return option.value === value;
  }, []);

  const formatedSelection = useMemo(() => {
    if (!Array.isArray(selections)) return [];

    return selections.map((item) => ({
      id: item?.id || item?.objectID,
      value: item?.value ?? item?.id ?? item?.objectID,
      label: item[`label_${currentLang}`] || item?.label || item?.name || '',
      name: item?.displayName || item?.name,
      color: item?.color,
      icon: item?.icon,
      duration: item?.duration,
      options: item?.options || [],
      listId: getRandomString(3),
    }));
  }, [selections, currentLang]);

  const currentValue = useMemo(() => {
    if (!Array.isArray(value)) return [];
    // Find the full option objects for the selected values
    return formatedSelection.filter(option => value.includes(option.value));
  }, [value, formatedSelection]);



  return (
    <ErrorBoundary>
      <FormControl
        fullWidth={fullWidth}
        error={error}
        required={required}
        margin="normal"
        disabled={disabled}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            '& fieldset': {
              borderColor: error ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
            },
            '&:hover fieldset': {
              borderColor: error ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
            },
            '&.Mui-focused fieldset': {
              borderColor: error ? 'error.main' : 'primary.main',
            },
          },
        }}
      >
        <InputLabel
          shrink={true}
          required={required}
          error={error}
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? 'rgb(51,51,51)' : '#FFF',
            padding: '2px 5px 2px 5px',
            borderRadius: '10px',
          }}
        >
          {label}
        </InputLabel>
        <Autocomplete
          multiple
          value={currentValue}
          onChange={handleChange}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          options={formatedSelection}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              size={size}
              error={error}
              required={required}
              onBlur={onBlur}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option.value}
                label={getOptionLabel(option)}
                {...getTagProps({ index })}
                sx={{
                  backgroundColor: option.color || theme.palette.primary.main,
                  color: '#fff',
                  '& .MuiChip-deleteIcon': {
                    color: '#fff',
                    '&:hover': {
                      color: '#fff',
                      opacity: 0.8,
                    },
                  },
                }}
              />
            ))
          }
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Box component="span">{getOptionLabel(option)}</Box>
            </Box>
          )}
          disabled={disabled}
          size={size}
        />
        {help && (
          <FormHelperText error={error} sx={{ mt: 0.5 }}>
            {help}
          </FormHelperText>
        )}
      </FormControl>
    </ErrorBoundary>
  );
};

export default MultiSelect;
